import {
  andExpr,
  icontainsExpr,
  equalsExpr,
  Expr,
  fieldExpr,
  valExpr,
  iequalsExpr,
} from "shared-ts-utils-query";
import { IllegalArgumentError } from "shared-ts-utils-core";

export type StreetAddressString = string;
export const STREET_ADDRESS_PATTERN =
  /^[A-Za-zæøåÆØÅ\s-]+(?:\s+\d+(?:\s*[A-Za-zÆØÅæøå])?)?(?:[-\s][A-Za-z]\d{4})?$/;

export const isStreetAddressString = (
  value: unknown
): value is StreetAddressString => {
  if (typeof value !== "string") {
    return false;
  }
  return STREET_ADDRESS_PATTERN.test(value);
};

export const parseStreetAddress = (value: unknown): StreetAddressDto => {
  const x = parseStreetAddressOrUndef(value);
  if (x == null) {
    throw new IllegalArgumentError(
      "Not a street address: " + JSON.stringify(value)
    );
  }
  return x;
};

export const parseStreetAddressOrUndef = (
  value: unknown
): StreetAddressDto | undefined => {
  if (!isStreetAddressString(value)) {
    return undefined;
  }

  const s = (value as string).trim();

  // 1) Try to parse with dash-separated house section: "<street> <number><letter?>-<section>"
  //    Examples: "Veien 3B-H0101", "Veien 3-K0101", "Veien 3b-L0101"
  //    House section is of the form [A-Za-z]\d{4}
  let m = s.match(
    /^(.+?)\s+(\d+)\s*([A-Za-zÆØÅæøå]?)\s*-\s*([A-Za-z]\d{4})\s*$/
  );
  if (m) {
    const street = m[1].trim();
    const number = parseInt(m[2], 10);
    let letter = (m[3] ?? "").trim();
    const section = m[4].toUpperCase();
    // Normalize house letter to uppercase when section is present (tests expect this)
    if (letter.length > 0) {
      letter = letter.toUpperCase();
    }
    const dto: StreetAddressDto = {
      street,
      houseNumber: number,
      houseSection: section,
    };
    if (letter.length > 0) {
      dto.houseLetter = letter;
    }
    return dto;
  }

  // 2) Try to parse with space-separated house section without house letter: "<street> <number> <section>"
  //    Example: "Veien 3 U0101"
  m = s.match(/^(.+?)\s+(\d+)\s+([A-Za-z]\d{4})\s*$/);
  if (m) {
    const street = m[1].trim();
    const number = parseInt(m[2], 10);
    const section = m[3].toUpperCase();
    const dto: StreetAddressDto = {
      street,
      houseNumber: number,
      houseSection: section,
    };
    return dto;
  }

  // 3) Try to parse basic number and optional letter: "<street> <number><letter?>" or with space between number and letter
  //    Examples: "Baker Street 12", "Baker Street 12B", "Baker Street 12 B"
  const addressRegex = /^(.+?)\s+(\d+)\s*([A-Za-zÆØÅæøå]?)\s*$/;
  const match = s.match(addressRegex);
  if (match) {
    const street = match[1].trim();
    const number = parseInt(match[2], 10);
    const letter = (match[3] ?? "").trim();

    const dto: StreetAddressDto = {
      street,
      houseNumber: number,
    };
    if (letter.length > 0) {
      dto.houseLetter = letter;
    }
    return dto;
  }

  // 4) Street name only
  return { street: s };
};

export const parseStreetAddressExprOrUndef = (
  value: unknown
): Expr | undefined => {
  const dto = parseStreetAddressOrUndef(value);
  if (dto == null) {
    return undefined;
  }
  const streetAddress = new StreetAddress(dto);
  return streetAddress.toExpr();
};

export interface StreetAddressDto {
  street: string;
  houseNumber?: number;
  houseLetter?: string;
  houseSection?: string;
}

export interface StreetAddressContainerDto {
  streetAddress: StreetAddressDto;
}

export class StreetAddress {
  readonly street: string;
  readonly houseNumber?: number;
  readonly houseLetter?: string;
  readonly houseSection?: string;
  readonly asString: string;

  constructor(dto: StreetAddressDto, hideBuildingUnit?: boolean) {
    this.street = dto.street;
    this.houseNumber = dto.houseNumber;
    this.houseLetter = dto.houseLetter;
    this.houseSection = dto.houseSection;

    let asString = this.street;
    if (this.houseNumber != null) {
      asString += " " + this.houseNumber;
      if (this.houseLetter != null && this.houseLetter.length > 0) {
        asString += " " + this.houseLetter;
      }
    }
    if (this.houseSection != null && hideBuildingUnit !== true) {
      asString += "-" + this.houseSection;
    }
    this.asString = asString;
  }

  toString(): string {
    return this.asString;
  }

  toExpr(): Expr {
    const streetExpr = icontainsExpr(
      fieldExpr("streetAddress.street"),
      valExpr(this.street)
    );

    if (this.houseNumber != null) {
      const numberExpr = equalsExpr(
        fieldExpr("streetAddress.houseNumber"),
        valExpr(this.houseNumber)
      );

      let combined: Expr = andExpr(streetExpr, numberExpr);

      if (this.houseLetter != null && this.houseLetter.length > 0) {
        const letterExpr = iequalsExpr(
          fieldExpr("streetAddress.houseLetter"),
          valExpr(this.houseLetter)
        );
        combined = andExpr(combined, letterExpr);
      }

      if (this.houseSection != null && this.houseSection.length > 0) {
        const sectionExpr = iequalsExpr(
          fieldExpr("streetAddress.houseSection"),
          valExpr(this.houseSection)
        );
        combined = andExpr(combined, sectionExpr);
      }

      return combined;
    }

    // Street-only case
    return streetExpr;
  }
}
